/**
 * Pure composer + parser for a note's YAML-ish frontmatter block and body
 * (spec §3.2 / §4.1). Single source of truth for the note document wire format
 * shared across `notebookActions` (server read/write), the agent's file writes,
 * and the browser render path.
 *
 * The grammar is deliberately minimal — flat `key: value` string pairs between
 * two `---` fences — so the domain needs NO YAML library (domain purity). It is
 * NOT general YAML: no nesting, no lists, no block scalars, no anchors. Unknown
 * keys are preserved verbatim on round-trip (Obsidian forward-compat).
 *
 * Frontmatter is ADVISORY convention, never an authorization signal. Every
 * consumer must tolerate missing / garbled frontmatter, so `parseNoteDocument`
 * is TOTAL and LENIENT: it never throws and never loses the body. GCS object
 * `updated` (mtime) is authoritative for list ordering; frontmatter timestamps
 * are display-only (spec §3.2).
 *
 * Pure: no fs, no React, no Convex, no `new Date()` — timestamps are injected
 * by callers. Safe to import from the Convex bundle and the browser alike.
 */

/**
 * The four well-known frontmatter fields (spec §3.2). All optional and readonly
 * (advisory). `createdBy`/`lastEditedBy` are `"user" | "agent"` by convention
 * but we keep them as free strings on parse so a garbled provenance value never
 * forces a throw — normalization to the union is a display concern.
 */
export interface NotebookFrontmatter {
  readonly createdBy?: string;
  readonly createdAt?: string;
  readonly updatedAt?: string;
  readonly lastEditedBy?: string;
  /**
   * Any additional keys the agent (or a future Obsidian plugin) wrote, kept
   * verbatim so a round-trip never drops them. Keys here never collide with the
   * four well-known fields above (those are lifted out on parse).
   */
  readonly [key: string]: string | undefined;
}

const FENCE = "---";

/** The well-known keys, emitted first and in this order for stable output. */
const KNOWN_KEYS = [
  "createdBy",
  "createdAt",
  "updatedAt",
  "lastEditedBy",
] as const;

/**
 * Compose a note document: a frontmatter block between `---` fences followed by
 * the body. Well-known keys are emitted first (stable order), then any unknown
 * keys in insertion order. `undefined` values are skipped. If no keys survive,
 * the fences are still emitted (an explicit empty frontmatter) so the shape is
 * predictable — callers that want a bare body simply pass an empty object and
 * accept the leading fences, or bypass this composer.
 */
export function composeNoteDocument({
  frontmatter,
  body,
}: {
  frontmatter: NotebookFrontmatter;
  body: string;
}): string {
  const lines: string[] = [];

  for (const key of KNOWN_KEYS) {
    const value = frontmatter[key];
    if (value !== undefined) {
      lines.push(`${key}: ${value}`);
    }
  }

  for (const key of Object.keys(frontmatter)) {
    if ((KNOWN_KEYS as readonly string[]).includes(key)) continue;
    const value = frontmatter[key];
    if (value !== undefined) {
      lines.push(`${key}: ${value}`);
    }
  }

  return `${FENCE}\n${lines.join("\n")}${lines.length > 0 ? "\n" : ""}${FENCE}\n${body}`;
}

/**
 * Parse a raw note document into its frontmatter and body. TOTAL and LENIENT:
 *
 *   - No leading fence, or a malformed / unterminated block → the ENTIRE raw
 *     input is treated as the body and `frontmatter` is an empty object. The
 *     body is never lost.
 *   - Lines inside the block that are not `key: value` are skipped (not fatal).
 *   - Duplicate keys: last write wins.
 *   - Never throws.
 *
 * A frontmatter block is recognized ONLY when the very first line is exactly
 * `---` and a later line is exactly `---`; everything after that closing fence
 * (minus a single following newline) is the body.
 */
export function parseNoteDocument({ raw }: { raw: string }): {
  frontmatter: NotebookFrontmatter;
  body: string;
} {
  // Normalize CRLF so fence detection is newline-style agnostic; we keep the
  // body otherwise byte-identical (we only strip the one newline after the
  // closing fence).
  const text = raw.replace(/\r\n/g, "\n");

  // The opening fence must be the first line, exactly `---`.
  if (!(text === FENCE || text.startsWith(`${FENCE}\n`))) {
    return { frontmatter: {}, body: raw };
  }

  // A document that is only `---` (no closing fence) is malformed → all body.
  if (text === FENCE) {
    return { frontmatter: {}, body: raw };
  }

  const afterOpen = text.slice(FENCE.length + 1); // drop "---\n"
  const closeIdx = findClosingFence(afterOpen);
  if (closeIdx === -1) {
    // Unterminated frontmatter → degrade: whole original raw is the body.
    return { frontmatter: {}, body: raw };
  }

  const block = afterOpen.slice(0, closeIdx);
  // Body is everything after the closing fence line. `closeIdx` points at the
  // start of the `---` line; skip past that line and its trailing newline.
  const afterClose = afterOpen.slice(closeIdx + FENCE.length);
  const body = afterClose.startsWith("\n") ? afterClose.slice(1) : afterClose;

  const frontmatter = parseFrontmatterBlock(block);
  return { frontmatter, body };
}

/**
 * Find the index (within `s`) of a line consisting solely of `---`. Returns the
 * index of the first char of that fence, or -1 if there is none.
 */
function findClosingFence(s: string): number {
  const lines = s.split("\n");
  let offset = 0;
  for (const line of lines) {
    if (line === FENCE) return offset;
    offset += line.length + 1; // + newline
  }
  return -1;
}

/** Parse a frontmatter block body (between fences) into flat key/value pairs. */
function parseFrontmatterBlock(block: string): NotebookFrontmatter {
  const out: Record<string, string> = {};
  for (const rawLine of block.split("\n")) {
    const line = rawLine.trimEnd();
    if (line.trim().length === 0) continue; // blank line
    const colon = line.indexOf(":");
    if (colon === -1) continue; // not a key: value line — skip, don't throw
    const key = line.slice(0, colon).trim();
    if (key.length === 0) continue;
    const value = line.slice(colon + 1).trim();
    out[key] = value; // last-write-wins on duplicate keys
  }
  return out;
}

/**
 * Display-facing metadata for a note (spec §4.1). Derived, never stored:
 *   - `title` from the path (filename minus `.md`).
 *   - provenance / createdAt from advisory frontmatter (may be undefined).
 *   - `updatedAt` prefers the GCS object mtime (`objectUpdatedAt`, authoritative
 *     for ordering) and falls back to the advisory frontmatter value.
 */
export interface NoteMeta {
  readonly path: string;
  readonly title: string;
  readonly createdBy?: string;
  readonly createdAt?: string;
  readonly updatedAt: string;
}

/**
 * Derive `NoteMeta` from a path, its (advisory) frontmatter, and the GCS object
 * mtime. `title` is the final path segment with a trailing `.md` stripped. The
 * GCS mtime wins for `updatedAt` (authoritative for list ordering, spec §3.2);
 * frontmatter `updatedAt` is only a fallback when the caller has no object time.
 */
export function deriveNoteMeta({
  path,
  frontmatter,
  objectUpdatedAt,
}: {
  path: string;
  frontmatter: NotebookFrontmatter;
  objectUpdatedAt?: string;
}): NoteMeta {
  const segments = path.split("/");
  const last = segments[segments.length - 1] ?? path;
  const title = last.replace(/\.md$/i, "");

  return {
    path,
    title,
    createdBy: frontmatter.createdBy,
    createdAt: frontmatter.createdAt,
    updatedAt: objectUpdatedAt ?? frontmatter.updatedAt ?? "",
  };
}
